import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { tasksAPI, authAPI } from '../services/api';
import Modal from '../components/Modal/Modal';
import { 
  Plus, 
  CheckSquare, 
  Edit, 
  Trash2, 
  Check,
  X
} from 'lucide-react';
import { toast } from 'react-toastify';

const Tasks = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filter, setFilter] = useState('my-todo'); // my-todo, partner-todo, completed
  const [editingTask, setEditingTask] = useState(null);
  const [completingTask, setCompletingTask] = useState(null); // Track which task is being completed
  
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    pookiePoints: 10,
    emoji: '💕',
    isRecurring: false,
    recurringFrequency: 'daily',
  });

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, []);

  // Scroll to top when page loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await tasksAPI.getTasks();
      if (response.data.success) {
        setTasks(response.data.tasks);
      }
    } catch (error) {
      toast.error('Error loading tasks! 😢');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await authAPI.getUsers();
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    try {
      // Debug log to check the pookie points value
      console.log('Creating task with pookie points:', newTask.pookiePoints);
      
      // Get the partner user (not the current user)
      const partnerUser = users.find(u => u._id !== user.id);
      
      if (!partnerUser) {
        toast.error('Partner user not found! 😢');
        return;
      }

      // Create task with partner as assignedTo
      const taskData = {
        ...newTask,
        assignedTo: partnerUser._id
      };

      console.log('Task data being sent:', taskData);
      const response = await tasksAPI.createTask(taskData);
      if (response.data.success) {
        setTasks([response.data.task, ...tasks]);
        setNewTask({
          title: '',
          description: '',
          pookiePoints: 10,
          emoji: '💕',
          isRecurring: false,
          recurringFrequency: 'daily',
        });
        setShowCreateModal(false);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error creating task! 😢');
    }
  };

  const handleCompleteTask = async (taskId) => {
    // Prevent multiple completions
    if (completingTask === taskId) return;
    
    try {
      setCompletingTask(taskId);
      const response = await tasksAPI.completeTask(taskId);
      
      if (response.data.success) {
        // Update the specific task in state with the response data
        setTasks(prevTasks => prevTasks.map(task => 
          task._id === taskId ? {
            ...task,
            ...response.data.task,
            isCompleted: true,
            completedAt: response.data.task.completedAt || new Date().toISOString(),
            confirmedBy: response.data.task.confirmedBy || task.createdBy,
            confirmedAt: response.data.task.confirmedAt || new Date().toISOString()
          } : task
        ));
        
        // Refetch tasks from database to ensure consistency
        setTimeout(() => {
          fetchTasks();
        }, 500);
        
        toast.success(response.data.message);
      } else {
        throw new Error(response.data.message || 'Failed to complete task');
      }
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error(error.response?.data?.message || 'Error completing task! 😢');
      
      // Refetch tasks on error to ensure UI is consistent with database
      fetchTasks();
    } finally {
      setCompletingTask(null);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (window.confirm('Are you sure you want to delete this task? 🗑️')) {
      try {
        const response = await tasksAPI.deleteTask(taskId);
        if (response.data.success) {
          setTasks(tasks.filter(task => task._id !== taskId));
          toast.success(response.data.message);
        }
      } catch (error) {
        toast.error(error.response?.data?.message || 'Error deleting task! 😢');
      }
    }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const response = await tasksAPI.updateTask(editingTask._id, editingTask);
      if (response.data.success) {
        setTasks(tasks.map(task => 
          task._id === editingTask._id ? response.data.task : task
        ));
        setEditingTask(null);
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error updating task! 😢');
    }
  };

  const getFilteredTasks = () => {
    switch (filter) {
      case 'completed':
        return tasks.filter(task => task.isCompleted);
      case 'my-todo':
        return tasks.filter(task => task.assignedTo._id === user.id); // Tasks assigned TO me (I need to do)
      case 'partner-todo':
        return tasks.filter(task => task.assignedTo._id !== user.id); // Tasks assigned TO partner (they need to do)
      default:
        return tasks;
    }
  };

  // Get the other user's name for the filter
  const getOtherUserName = () => {
    const otherUser = users.find(u => u._id !== user.id);
    return otherUser ? otherUser.displayName : 'Partner';
  };

  const TaskCard = ({ task }) => {
    const canComplete = task.createdBy._id === user.id && !task.isCompleted;
    const canEdit = task.createdBy._id === user.id && !task.isCompleted;
    const canDelete = task.createdBy._id === user.id;
    const isCompleting = completingTask === task._id;

    return (
      <div className={`bg-white rounded-xl border transition-all duration-200 hover:shadow-md ${
        task.isCompleted 
          ? 'opacity-50 border-green-200 bg-green-50' 
          : 'border-gray-100 hover:border-pookie-pink-200'
      }`}>
        {/* Task content */}
        <div className="p-3">
          <div className="flex items-start space-x-3 mb-3">
            {/* Emoji */}
            <div className="flex-shrink-0">
              <span className="text-xl">{task.emoji}</span>
            </div>
            
            {/* Task details */}
            <div className="flex-1 min-w-0">
              <h3 className={`font-medium text-sm line-clamp-1 mb-1 ${
                task.isCompleted ? 'text-green-800' : 'text-gray-900'
              }`}>
                {task.title}
              </h3>
              {task.description && (
                <p className={`text-xs mb-2 ${
                  task.isCompleted ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {task.description}
                </p>
              )}
              
              {/* Points and status in compact row */}
              <div className="flex items-center space-x-2">
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                  task.isCompleted 
                    ? 'bg-green-200 text-green-800' 
                    : 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-700'
                }`}>
                  💎 {task.pookiePoints}
                </div>
                {task.isRecurring && (
                  <div className="bg-blue-100 text-blue-600 px-2 py-1 rounded-full text-xs">
                    🔄 Recurring
                  </div>
                )}
              </div>
            </div>

            {/* Small action buttons for edit/delete - only show if not completed */}
            {!task.isCompleted && (
              <div className="flex flex-col space-y-1">
                {canEdit && (
                  <button
                    onClick={() => setEditingTask(task)}
                    className="bg-blue-500 hover:bg-blue-600 text-white p-1.5 rounded-lg transition-colors"
                    title="Edit task"
                  >
                    <Edit className="w-3 h-3" />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => handleDeleteTask(task._id)}
                    className="bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-lg transition-colors"
                    title="Delete task"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Big completion button or completion status */}
          {task.isCompleted ? (
            <div className="bg-green-200 border border-green-300 rounded-lg p-3 flex items-center space-x-2">
              <Check className="w-4 h-4 text-green-700" />
              <span className="text-sm font-medium text-green-800">
                {task.assignedTo.displayName} Completed the Task
              </span>
            </div>
          ) : canComplete ? (
            <button
              onClick={() => handleCompleteTask(task._id)}
              disabled={isCompleting}
              className={`w-full p-3 rounded-lg transition-colors flex items-center justify-center space-x-2 font-medium ${
                isCompleting 
                  ? 'bg-gray-400 cursor-not-allowed text-white' 
                  : 'bg-green-500 hover:bg-green-600 text-white'
              }`}
            >
              {isCompleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Completing...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>Mark {task.assignedTo.displayName} Completed</span>
                </>
              )}
            </button>
          ) : (
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                Waiting for {task.assignedTo.displayName} to complete...
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const filteredTasks = getFilteredTasks();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pookie-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-mobile-y px-mobile pb-20 lg:pb-0">
      {/* Mobile-optimized header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-mobile-3xl font-bold text-gradient mb-2">Tasks</h1>
          <p className="text-mobile-base text-gray-600">Manage your cute little tasks! 💕</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden xs:inline">Create Task</span>
        </button>
      </div>

      {/* Simplified filters - only 3 options */}
      <div className="flex flex-wrap gap-2 hide-scrollbar overflow-x-auto pb-2">
        <button
          onClick={() => setFilter('my-todo')}
          className={`px-3 py-2 rounded-full text-mobile-sm transition-all whitespace-nowrap ${
            filter === 'my-todo'
              ? 'bg-pookie-pink-500 text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-pookie-pink-50'
          }`}
        >
          My To-Do
        </button>
        <button
          onClick={() => setFilter('partner-todo')}
          className={`px-3 py-2 rounded-full text-mobile-sm transition-all whitespace-nowrap ${
            filter === 'partner-todo'
              ? 'bg-pookie-pink-500 text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-pookie-pink-50'
          }`}
        >
          {getOtherUserName()} To-Do
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`px-3 py-2 rounded-full text-mobile-sm transition-all whitespace-nowrap ${
            filter === 'completed'
              ? 'bg-pookie-pink-500 text-white shadow-md'
              : 'bg-white text-gray-600 hover:bg-pookie-pink-50'
          }`}
        >
          Completed
        </button>
      </div>

      {/* Mobile-optimized tasks grid - single column on mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {filteredTasks.map((task) => (
          <TaskCard key={task._id} task={task} />
        ))}
      </div>

      {filteredTasks.length === 0 && (
        <div className="text-center py-12">
          <CheckSquare className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-mobile-xl font-semibold text-gray-600 mb-2">No tasks found</h3>
          <p className="text-mobile-base text-gray-500">Create your first task to get started! 💕</p>
        </div>
      )}

      {/* Create Task Modal */}
      {showCreateModal && (
        <Modal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          title="Create New Task"
          actions={
            <div className="flex space-x-3">
              <button
                type="submit"
                form="create-task-form"
                className="btn-primary flex-1"
              >
                Create Task
              </button>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          }
        >
          <form id="create-task-form" onSubmit={handleCreateTask} className="space-y-4" key="create-task-form-v2">
            <div>
              <label className="label-cute">Task Title</label>
              <input
                type="text"
                value={newTask.title}
                onChange={(e) => setNewTask({...newTask, title: e.target.value})}
                className="input-cute"
                placeholder="What needs to be done?"
                required
              />
            </div>

            <div>
              <label className="label-cute">Description</label>
              <textarea
                value={newTask.description}
                onChange={(e) => setNewTask({...newTask, description: e.target.value})}
                className="input-cute"
                placeholder="Describe the task..."
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-cute">Pookie Points</label>
                <input
                  type="number"
                  value={newTask.pookiePoints}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 1 : Math.max(1, Math.min(5000, parseInt(e.target.value) || 1));
                    setNewTask({...newTask, pookiePoints: value});
                  }}
                  className="input-cute"
                  min="1"
                  max="5000"
                  step="1"
                  placeholder="Enter points (1-5000)"
                  required
                />
              </div>

              <div>
                <label className="label-cute">Emoji</label>
                <input
                  type="text"
                  value={newTask.emoji}
                  onChange={(e) => setNewTask({...newTask, emoji: e.target.value})}
                  className="input-cute"
                  placeholder="💕"
                  maxLength="2"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={newTask.isRecurring}
                  onChange={(e) => setNewTask({...newTask, isRecurring: e.target.checked})}
                  className="checkbox-cute"
                />
                <span className="label-cute mb-0">Make this a recurring task</span>
              </label>
            </div>

            {newTask.isRecurring && (
              <div>
                <label className="label-cute">Repeat Frequency</label>
                <select
                  value={newTask.recurringFrequency}
                  onChange={(e) => setNewTask({...newTask, recurringFrequency: e.target.value})}
                  className="input-cute"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            )}
          </form>
        </Modal>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <Modal
          isOpen={!!editingTask}
          onClose={() => setEditingTask(null)}
          title="Edit Task"
          actions={
            <div className="flex space-x-3">
              <button
                type="submit"
                form="edit-task-form"
                className="btn-primary flex-1"
              >
                Update Task
              </button>
              <button
                type="button"
                onClick={() => setEditingTask(null)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          }
        >
          <form id="edit-task-form" onSubmit={handleUpdateTask} className="space-y-4" key={`edit-task-form-${editingTask?._id}`}>
            <div>
              <label className="label-cute">Task Title</label>
              <input
                type="text"
                value={editingTask.title}
                onChange={(e) => setEditingTask({...editingTask, title: e.target.value})}
                className="input-cute"
                placeholder="What needs to be done?"
                required
              />
            </div>

            <div>
              <label className="label-cute">Description</label>
              <textarea
                value={editingTask.description}
                onChange={(e) => setEditingTask({...editingTask, description: e.target.value})}
                className="input-cute"
                placeholder="Describe the task..."
                rows={3}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label-cute">Pookie Points</label>
                <input
                  type="number"
                  value={editingTask.pookiePoints}
                  onChange={(e) => {
                    const value = e.target.value === '' ? 1 : Math.max(1, Math.min(5000, parseInt(e.target.value) || 1));
                    setEditingTask({...editingTask, pookiePoints: value});
                  }}
                  className="input-cute"
                  min="1"
                  max="5000"
                  step="1"
                  placeholder="Enter points (1-5000)"
                  required
                />
              </div>

              <div>
                <label className="label-cute">Emoji</label>
                <input
                  type="text"
                  value={editingTask.emoji}
                  onChange={(e) => setEditingTask({...editingTask, emoji: e.target.value})}
                  className="input-cute"
                  placeholder="💕"
                  maxLength="2"
                />
              </div>
            </div>

            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={editingTask.isRecurring}
                  onChange={(e) => setEditingTask({...editingTask, isRecurring: e.target.checked})}
                  className="checkbox-cute"
                />
                <span className="label-cute mb-0">Make this a recurring task</span>
              </label>
            </div>

            {editingTask.isRecurring && (
              <div>
                <label className="label-cute">Repeat Frequency</label>
                <select
                  value={editingTask.recurringFrequency}
                  onChange={(e) => setEditingTask({...editingTask, recurringFrequency: e.target.value})}
                  className="input-cute"
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
};

export default Tasks; 